import { Tag, Space, Typography, Flex, Tooltip } from "antd"
import NumberFormat from "react-number-format"
import {
  UserOutlined,
  SwapOutlined,
  MinusCircleOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  CalculatorOutlined,
} from "@ant-design/icons"

const { Text } = Typography

// Helper component for displaying values in rows
const ValueRow = ({ label, value, color }) => (
  <Flex justify="space-between" align="center" style={{ marginBottom: 2 }}>
    <Text type="secondary" style={{ fontSize: 10 }}>{label}</Text>
    <NumberFormat
      displayType="text"
      value={value}
      thousandSeparator={true}
      renderText={val => (
        <Text strong style={{ fontSize: 11, color }}>{val}</Text>
      )}
    />
  </Flex>
)

// Card-style value display for mobile
const MobileValueCard = ({ title, thisQuarter, beforeQuarter, icon, color }) => (
  <div
    style={{
      background: "#fafafa",
      borderRadius: 6,
      padding: "6px 8px",
      marginBottom: 4,
    }}
  >
    <Flex align="center" gap={4} style={{ marginBottom: 4 }}>
      {icon}
      <Text type="secondary" style={{ fontSize: 10 }}>{title}</Text>
    </Flex>
    <Flex justify="space-between">
      <div>
        <Text type="secondary" style={{ fontSize: 9 }}>این دوره: </Text>
        <NumberFormat
          displayType="text"
          value={thisQuarter}
          thousandSeparator={true}
          renderText={val => <Text strong style={{ fontSize: 11, color }}>{val}</Text>}
        />
      </div>
      <div>
        <Text type="secondary" style={{ fontSize: 9 }}>سال: </Text>
        <NumberFormat
          displayType="text"
          value={beforeQuarter}
          thousandSeparator={true}
          renderText={val => <Text style={{ fontSize: 10 }}>{val}</Text>}
        />
      </div>
    </Flex>
  </div>
)

export const engWorkColumnsAnt = ({ props, matches_sm, period, year }) => {
  // Mobile: 2 columns
  if (matches_sm) {
    return [
      {
        title: "مهندس",
        key: "fullName",
        width: 140,
        render: params => (
          <div
            style={{
              background: "#f6f8fa",
              borderRadius: 8,
              padding: 8,
              border: "1px solid #e8e8e8",
            }}
          >
            <Flex align="center" gap={6} style={{ marginBottom: 6 }}>
              <UserOutlined style={{ color: "#667eea" }} />
              <Text strong style={{ fontSize: 12 }}>{params.fullName}</Text>
            </Flex>
            <Tag color="blue" style={{ fontSize: 9, borderRadius: 4 }}>
              دوره {period} - سال {year}
            </Tag>
          </div>
        ),
      },
      {
        title: "کارکرد",
        key: "work",
        width: 200,
        render: params => (
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <MobileValueCard
              title="تخصیص"
              thisQuarter={params.sumAmountEngQuota}
              beforeQuarter={params.sumAmountEngQuotaBeforePeriod}
              icon={<SwapOutlined style={{ color: "#1890ff", fontSize: 10 }} />}
              color="#1890ff"
            />
            <MobileValueCard
              title="کسورات"
              thisQuarter={params.sumAmountEngQuotaBurn}
              beforeQuarter={params.sumAmountEngQuotaBurnBefore}
              icon={<MinusCircleOutlined style={{ color: "#ff4d4f", fontSize: 10 }} />}
              color="#ff4d4f"
            />
            <MobileValueCard
              title="ارجاعی"
              thisQuarter={params.sumAmountEngProcessFee}
              beforeQuarter={params.sumAmountEngProcessFeeBeforePeriod}
              icon={<FileTextOutlined style={{ color: "#fa8c16", fontSize: 10 }} />}
              color="#fa8c16"
            />
            <MobileValueCard
              title="انجام شده"
              thisQuarter={params.sumAmountEngRealWorkThisQuarter}
              beforeQuarter={params.sumAmountEngRealWorkSelectedYear}
              icon={<CheckCircleOutlined style={{ color: "#52c41a", fontSize: 10 }} />}
              color="#52c41a"
            />
          </Space>
        ),
      },
    ]
  }

  // Desktop: all columns
  return [
    {
      title: (
        <Flex align="center" gap={4}>
          <UserOutlined style={{ color: "#667eea" }} />
          <span>نام مهندس</span>
        </Flex>
      ),
      key: "fullName",
      dataIndex: "fullName",
      width: 150,
      fixed: "right",
      render: name => (
        <Text strong style={{ fontSize: 12 }}>{name}</Text>
      ),
    },
    {
      title: (
        <Tooltip title={`تخصیص این دوره / در سال ${year}`}>
          <Flex align="center" gap={4}>
            <SwapOutlined style={{ color: "#1890ff" }} />
            <span>تخصیص</span>
          </Flex>
        </Tooltip>
      ),
      key: "sumAmountEngQuota",
      width: 140,
      render: params => (
        <div style={{ background: "#e6f7ff", borderRadius: 6, padding: "4px 8px" }}>
          <ValueRow label="این دوره" value={params.sumAmountEngQuota} color="#1890ff" />
          <ValueRow label={`سال ${year}`} value={params.sumAmountEngQuotaBeforePeriod} color="#8c8c8c" />
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`کسورات سهمیه این دوره / در سال ${year}`}>
          <Flex align="center" gap={4}>
            <MinusCircleOutlined style={{ color: "#ff4d4f" }} />
            <span>کسورات</span>
          </Flex>
        </Tooltip>
      ),
      key: "sumAmountEngQuotaBurn",
      width: 140,
      render: params => (
        <div style={{ background: "#fff2f0", borderRadius: 6, padding: "4px 8px" }}>
          <ValueRow label="این دوره" value={params.sumAmountEngQuotaBurn} color="#ff4d4f" />
          <ValueRow label={`سال ${year}`} value={params.sumAmountEngQuotaBurnBefore} color="#8c8c8c" />
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`مانده تخصیص بعد از کسورات این دوره / در سال ${year}`}>
          <Flex align="center" gap={4}>
            <CalculatorOutlined style={{ color: "#722ed1" }} />
            <span>مانده تخصیص</span>
          </Flex>
        </Tooltip>
      ),
      key: "sumAmountEngQuotaBurn2",
      width: 140,
      render: params => (
        <div style={{ background: "#f9f0ff", borderRadius: 6, padding: "4px 8px" }}>
          <ValueRow
            label="این دوره"
            value={params.sumAmountEngQuota - params.sumAmountEngQuotaBurn}
            color="#722ed1"
          />
          <ValueRow
            label={`سال ${year}`}
            value={params.sumAmountEngQuotaBeforePeriod - params.sumAmountEngQuotaBurnBefore}
            color="#8c8c8c"
          />
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`ارجاعی این دوره / در سال ${year}`}>
          <Flex align="center" gap={4}>
            <FileTextOutlined style={{ color: "#fa8c16" }} />
            <span>ارجاعی</span>
          </Flex>
        </Tooltip>
      ),
      key: "sumAmountEngProcessFeeBeforePeriod",
      width: 140,
      render: params => (
        <div style={{ background: "#fff7e6", borderRadius: 6, padding: "4px 8px" }}>
          <ValueRow label="این دوره" value={params.sumAmountEngProcessFee} color="#fa8c16" />
          <ValueRow label={`سال ${year}`} value={params.sumAmountEngProcessFeeBeforePeriod} color="#8c8c8c" />
        </div>
      ),
    },
    {
      title: (
        <Tooltip title={`انجام شده این دوره / در سال ${year}`}>
          <Flex align="center" gap={4}>
            <CheckCircleOutlined style={{ color: "#52c41a" }} />
            <span>انجام شده</span>
          </Flex>
        </Tooltip>
      ),
      key: "sumAmountEngRealWorkThisQuarter",
      width: 140,
      render: params => (
        <div style={{ background: "#f6ffed", borderRadius: 6, padding: "4px 8px" }}>
          <ValueRow label="این دوره" value={params.sumAmountEngRealWorkThisQuarter} color="#52c41a" />
          <ValueRow label={`سال ${year}`} value={params.sumAmountEngRealWorkSelectedYear} color="#8c8c8c" />
        </div>
      ),
    },
  ].filter(item => !item.hidden)
}
